const API_BASE = "/api";

function getAccessToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("de_access_token");
}

function getRefreshToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("de_refresh_token");
}

// Single in-flight refresh shared across concurrent 401s. Without this,
// the ~6 parallel calls a page fires on load would each POST /auth/refresh
// with the same (rotating) refresh token — only the first succeeds and the
// rest invalidate the token. De-duping keeps one refresh authoritative.
let refreshInFlight: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  const rt = getRefreshToken();
  if (!rt) return false;
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: rt }),
          cache: "no-store",
        });
        if (!res.ok) return false;
        const data = (await res.json()) as {
          access_token?: string;
          refresh_token?: string;
        };
        const at = data.access_token;
        if (!at) return false;
        localStorage.setItem("de_access_token", at);
        if (data.refresh_token) {
          localStorage.setItem("de_refresh_token", data.refresh_token);
        }
        return true;
      } catch {
        return false;
      }
    })();
    void refreshInFlight.finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function authFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const send = (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return fetch(url, {
      ...options,
      headers,
      cache: options.cache ?? "no-store",
    });
  };

  let res = await send(getAccessToken());

  // Transparent recovery: an expired access token returns 401. Refresh once
  // (deduped) and retry, so mutations don't silently die after the 15-min
  // access-token TTL — the failure mode that made action chips look "dead".
  if (res.status === 401 && getRefreshToken()) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      res = await send(getAccessToken());
    }
  }

  return res;
}

async function readJson<T>(response: Response, fallbackMessage: string): Promise<T> {
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const detail = err?.detail;
    let detailMessage: string | null = null;
    if (typeof detail === "string") {
      detailMessage = detail;
    } else if (Array.isArray(detail) && detail.length > 0) {
      detailMessage = detail
        .map((d) => {
          if (typeof d === "string") return d;
          if (d && typeof d === "object" && "msg" in d) {
            const loc = Array.isArray((d as { loc?: unknown[] }).loc)
              ? (d as { loc: unknown[] }).loc.join(".")
              : "";
            return loc ? `${loc}: ${(d as { msg: string }).msg}` : (d as { msg: string }).msg;
          }
          return JSON.stringify(d);
        })
        .join("; ");
    }
    throw new Error(`${detailMessage || fallbackMessage} (HTTP ${response.status})`);
  }
  return (await response.json()) as T;
}

export { API_BASE, authFetch, getAccessToken, readJson };
